import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IMicaChatTelefonoData } from '@core/interfaces/entidades/telefono.entidad';
import { MicaChatEnviarMensajeReqDTO, MicaChatSubirArchivoResponse } from '@core/interfaces/request-response/micachat.dto.interface';
import { environment } from '@env/environment';
import { MicaChat_ChatsEntity } from '@modules/mica-chat/entities/chats.mica-chat';
import { MicaChat_MensajesEntity } from '@modules/mica-chat/entities/mensajes.mica-chat';
import { BaseHttpService } from '@shared/services/base-http.service';
import {
  IFlujoWhatsapp,
  ICrearFlujoReq,
  IEditarFlujoReq,
  ICrearPasoReq,
  IEditarPasoReq,
  IPasoFlujo,
} from '@core/interfaces/request-response/flujo.dto.interface';

@Injectable({ providedIn: 'root' })
export class MicaChatApiService extends BaseHttpService {
  private readonly base = `${environment.apiMicaChatUrl}${environment.apiPreFix}/mica-chat`;

  cargarTelefonos() {
    return this.withLoader(
      this.httpClient.get<IMicaChatTelefonoData[]>(`${this.base}/telefono/cargar-sucursal`),
    );
  }

  cargarChats(telefonoId: number) {
    return this.withLoader(
      this.httpClient.get<MicaChat_ChatsEntity[]>(`${this.base}/chats/cargar/${telefonoId}`),
    );
  }

  cargarMensajes(chatId: number) {
    return this.withLoader(
      this.httpClient.get<MicaChat_MensajesEntity[]>(`${this.base}/mensajes/cargar/${chatId}`),
    );
  }

  marcarChatComoLeido(chatId: number) {
    return this.httpClient.post<void>(`${this.base}/chats/${chatId}/marcar-leido`, {});
  }

  enviarMensaje(telefonoId: number, dto: MicaChatEnviarMensajeReqDTO) {
    return this.httpClient.post(`${this.base}/telefono/enviar-mensaje/${telefonoId}`, dto);
  }

  subirArchivo(formData: FormData): Observable<HttpEvent<MicaChatSubirArchivoResponse>> {
    const req = new HttpRequest('POST', `${this.base}/archivos/subir`, formData, { reportProgress: true });
    return this.httpClient.request<MicaChatSubirArchivoResponse>(req);
  }

  subirAudioGrabado(formData: FormData): Observable<HttpEvent<MicaChatSubirArchivoResponse>> {
    const req = new HttpRequest('POST', `${this.base}/archivos/subir-audio`, formData, { reportProgress: true });
    return this.httpClient.request<MicaChatSubirArchivoResponse>(req);
  }

  toggleComprobantes(telefonoId: number, activar: boolean) {
    return this.httpClient.patch<{ telefonoId: number; sucursalId: number; usar_para_comprobantes: boolean }>(
      `${this.base}/telefono/toggle-comprobantes/${telefonoId}`,
      { activar },
    );
  }

  listarFlujos(telefonoId: number) {
    return this.httpClient.get<IFlujoWhatsapp[]>(`${this.base}/flujo/listar/${telefonoId}`);
  }

  obtenerFlujo(flujoId: number) {
    return this.httpClient.get<IFlujoWhatsapp>(`${this.base}/flujo/detalle/${flujoId}`);
  }

  crearFlujo(telefonoId: number, dto: ICrearFlujoReq) {
    return this.httpClient.post<IFlujoWhatsapp>(`${this.base}/flujo/crear/${telefonoId}`, dto);
  }

  editarFlujo(flujoId: number, dto: IEditarFlujoReq) {
    return this.httpClient.patch<IFlujoWhatsapp>(`${this.base}/flujo/editar/${flujoId}`, dto);
  }

  toggleFlujo(flujoId: number) {
    return this.httpClient.patch<IFlujoWhatsapp>(`${this.base}/flujo/toggle/${flujoId}`, {});
  }

  eliminarFlujo(flujoId: number) {
    return this.httpClient.delete(`${this.base}/flujo/eliminar/${flujoId}`);
  }

  agregarPaso(flujoId: number, dto: ICrearPasoReq) {
    return this.httpClient.post<IPasoFlujo>(`${this.base}/flujo/${flujoId}/paso`, dto);
  }

  editarPaso(flujoId: number, pasoId: number, dto: IEditarPasoReq) {
    return this.httpClient.patch<IPasoFlujo>(`${this.base}/flujo/${flujoId}/paso/${pasoId}`, dto);
  }

  eliminarPaso(flujoId: number, pasoId: number) {
    return this.httpClient.delete(`${this.base}/flujo/${flujoId}/paso/${pasoId}`);
  }

  reordenarPasos(flujoId: number, ordenes: { id: number; orden: number }[]) {
    return this.httpClient.patch(`${this.base}/flujo/${flujoId}/reordenar`, ordenes);
  }

  subirFlujoMedia(formData: FormData): Observable<HttpEvent<{ url: string; mimeType: string; fileName: string }>> {
    const req = new HttpRequest('POST', `${this.base}/archivos/subir-flujo-media`, formData, { reportProgress: true });
    return this.httpClient.request<{ url: string; mimeType: string; fileName: string }>(req);
  }
}
